import axios, { AxiosResponse } from 'axios';
import { getItem } from '../utils/localStorage';
import { IMyAudit } from '../app/@types/myAudit';
import { IGetMyAuditList, IMyAuditListParams } from 'src/app/@types/myAudit';
import axiosInstance from '../helpers/interceptor';
import { userHotelDetails } from '../helpers/handleBrandAndHotelData';
import { ITemplateSearchRequest } from 'src/app/@types/myAuditList';

export const getMyAuditTemplates = async (payload) => {
  const { selectedHotel, selectedBrand } = userHotelDetails();
  const { searchTerm, selectedDepartment, pageNumber } = payload;
  const hotelIdParam =
    payload.filterType === undefined || payload.filterType === 'hotel'
      ? `&hotelId=${selectedHotel}`
      : '';
  const brandIdParam =
    payload.filterType === undefined || payload.filterType === 'brand'
      ? `&brandId=${selectedBrand}`
      : '';
  const searchParam = searchTerm ? `&search=${searchTerm}` : '';
  const departmentParam = selectedDepartment
    ? `&categoryId=${selectedDepartment}`
    : '';

  return axiosInstance.get(
    `${Env.API_URL}/myaudit-templates?sort=id&pageSize=20&isActive=true&page=${pageNumber}${hotelIdParam}${brandIdParam}${searchParam}${departmentParam}`
  );
};

export const getSearchedMyAuditTemplates = async (
  payload: ITemplateSearchRequest
) => {
  const { accessToken } = getItem('user');
  const selectedHotel = getItem('selectedHotel');
  const selectedBrand = getItem('selectedBrand');
  const { searchTerm, pageNumber } = payload;
  const hotelIdParam = `&hotelId=${selectedHotel}`;
  const brandIdParam = `&brandId=${selectedBrand}`;
  const searchParam = searchTerm ? `&templateName=${searchTerm}` : '';

  return axios.get(
    `${Env.API_URL}/myaudit-templates/template-name-search?pageSize=20&page=${pageNumber}&sort=templateName${hotelIdParam}${brandIdParam}${searchParam}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getMyAuditList = async ({
  page,
  multiUserLookupType,
  multiUserLookupIds,
  multiUserLookupLogic,
  sort,
  status,
  startDate,
  endDate,
  templateId,
  myAuditId,
}: IGetMyAuditList) => {
  const { selectedHotel } = userHotelDetails();
  const hotelIdParam = selectedHotel && `hotelId=${selectedHotel}`;
  const { user } = getItem('user');
  const id = user.id;
  const params: IMyAuditListParams = {
    page,
    pageSize: 10,
    sort,
    userId: id,
    multiUserLookupType,
    templateId,
    myAuditId,
  };

  if (myAuditId) {
    params.myAuditId = myAuditId;
  }

  if (status) {
    params.status = status;
  }

  if (multiUserLookupIds && multiUserLookupLogic) {
    params.multiUserLookupIds = multiUserLookupIds;
    params.multiUserLookupLogic = multiUserLookupLogic;
  }

  if (startDate && endDate) {
    params.startDate = startDate;
    params.endDate = endDate;
  }

  return axiosInstance.get(`${Env.API_URL}/myaudit/list?${hotelIdParam}`, {
    params,
  });
};

export const getMyAuditTemplatesDeptList = async () => {
  const { selectedHotel, selectedBrand } = userHotelDetails();

  const hotelIdParam = selectedHotel && `hotelId=${selectedHotel}`;
  const brandIdParam = selectedBrand && `&brandId=${selectedBrand}`;

  return axiosInstance.get(
    `${Env.API_URL}/myaudit-templates/get-department-list?${hotelIdParam}${brandIdParam}`
  );
};

export const getMyAuditTemplateCategories = async (templateId: number) => {
  return axiosInstance.get(
    `${Env.API_URL}/myaudit-templates/${templateId}/get-category`
  );
};

export const getMyAuditCounts = async () => {
  const { selectedHotel } = userHotelDetails();

  const hotelIdParam = selectedHotel && `hotelId=${selectedHotel}`;

  return axiosInstance.get(`${Env.API_URL}/myaudit/counts?${hotelIdParam}`);
};

export const getMyAuditSummary = async (myAuditId: number) => {
  return axiosInstance.get(`${Env.API_URL}/myaudit/summary/${myAuditId}`);
};

export const createMyAudit = async (payload) => {
  const { accessToken } = getItem('user');

  return axios.post(`${Env.API_URL}/myaudit`, payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const deleteMyAuditRequest = async (id: number) => {
  const { accessToken } = getItem('user');

  return axios.delete(`${Env.API_URL}/myaudit/${id}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const getMyAudit = async (
  id: number,
  action: 'conduct' | 'view'
): Promise<AxiosResponse<IMyAudit>> => {
  return axiosInstance.get(
    `${Env.API_URL}/myaudit/view/${id}?action=${action}`
  );
};

export const saveMyAudit = async (id: number, payload) => {
  const { accessToken } = getItem('user');

  return axios.put(`${Env.API_URL}/myaudit/${id}`, payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const submitMyAudit = async (id: number, payload) => {
  const { accessToken } = getItem('user');

  return axios.put(`${Env.API_URL}/myaudit/${id}/submit`, payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const saveMyAuditAttachment = async (
  file,
  auditId,
  sectionId?: string | number,
  standardId?: string | number
) => {
  const { accessToken } = getItem('user');

  return axios.post(
    `${
      Env.API_URL
    }/myaudit-attachment/${auditId}/attachFile?sectionId=${sectionId}${
      standardId ? '&standardId=' + standardId : ''
    }`,
    file,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const deleteMyAuditAttachment = async (auditId, fileId) => {
  const { accessToken } = getItem('user');

  return axios.delete(
    `${Env.API_URL}/myaudit-attachment/${auditId}/delete-attachment/${fileId}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};
export const getMyAuditDetails = async (id: number) => {
  return axiosInstance.get(`${Env.API_URL}/myaudit/details/${id}`);
};
